import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
    FaPenNib, FaVideo, FaLightbulb, FaBullseye, FaPencilRuler, FaLayerGroup,
    FaCheckCircle, FaChevronDown, FaChevronUp, FaChevronRight, FaPlayCircle, FaPodcast,
    FaMedium, FaWordpress, FaYoutube, FaNewspaper, FaPalette, FaImages, FaBullhorn
} from 'react-icons/fa';
import heroImage from '../../assets/Kernel in Linux Operating System_.png';

const ContentMarketingPage = () => {
    const [openFaq, setOpenFaq] = useState(null);

    const toggleFaq = (index) => {
        setOpenFaq(openFaq === index ? null : index);
    };

    const services = [
        { icon: FaPenNib, title: 'Blog Writing', desc: 'SEO-optimized articles that rank high and establish your authority.', image: 'https://images.unsplash.com/photo-1499750310159-52f0f83ec631?w=600' },
        { icon: FaVideo, title: 'Video Production', desc: 'High-quality explainers, demos, and corporate videos.', image: 'https://images.unsplash.com/photo-1492691527719-9d1e07e534b4?w=600' },
        { icon: FaPencilRuler, title: 'Graphic Design', desc: 'Stunning infographics, social creatives, and brand assets.', image: 'https://images.unsplash.com/photo-1626785774573-4b799314346d?w=600' },
        { icon: FaBullseye, title: 'Copywriting', desc: 'Persuasive sales copy for landing pages, emails, and ads.', image: 'https://images.unsplash.com/photo-1519389950473-47ba0277781c?w=600' },
        { icon: FaPodcast, title: 'Scriptwriting', desc: 'Engaging scripts for podcasts, webinars, and speeches.', image: 'https://images.unsplash.com/photo-1478737270239-2f63b8625881?w=600' },
        { icon: FaLayerGroup, title: 'Content Strategy', desc: 'Data-driven content calendars aligned with your business goals.', image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600' }
    ];

    const platforms = [
        { name: 'Medium', icon: FaMedium, desc: 'Thought Leadership' },
        { name: 'WordPress', icon: FaWordpress, desc: 'Corporate Blogs' },
        { name: 'YouTube', icon: FaYoutube, desc: 'Video Content' },
        { name: 'Substack', icon: FaNewspaper, desc: 'Newsletters' },
        { name: 'Canva', icon: FaPalette, desc: 'Visual Design' },
        { name: 'Adobe CC', icon: FaImages, desc: 'Pro Design' },
        { name: 'HubSpot', icon: FaBullhorn, desc: 'Content CMS' },
        { name: 'Podcast', icon: FaPodcast, desc: 'Audio Content' }
    ];

    // Horizontal Flow Steps (Content Lifecycle)
    const flowSteps = [
        { title: 'Ideate', icon: FaLightbulb, color: 'from-yellow-400 to-orange-500' },
        { title: 'Research', icon: FaBullseye, color: 'from-red-500 to-pink-600' },
        { title: 'Create', icon: FaPenNib, color: 'from-purple-500 to-indigo-500' },
        { title: 'Edit', icon: FaPencilRuler, color: 'from-blue-500 to-cyan-500' },
        { title: 'Publish', icon: FaPlayCircle, color: 'from-green-500 to-emerald-500' },
        { title: 'Amplify', icon: FaLayerGroup, color: 'from-teal-400 to-cyan-600' }
    ];

    const faqs = [
        { q: 'What is Content Marketing?', a: 'It is a strategic marketing approach focused on creating and distributing valuable, relevant, and consistent content to attract and retain a clearly defined audience.' },
        { q: 'Does blogging still work?', a: 'Absolutely. Companies with blogs produce 67% more leads per month than those without. It is the backbone of SEO.' },
        { q: 'Do you create video content?', a: 'Yes, we have a full production team for animated explainers, live-action shoots, and social media shorts.' },
        { q: 'How do you measure success?', a: 'We track page views, time on page, social shares, and most importantly, the leads and conversions generated from the content.' },
        { q: 'Can you prefer technical writing?', a: 'Yes, we have subject matter experts who can write deep-dive technical whitepapers and documentation.' }
    ];

    const whyChooseUs = [
        { title: 'Storytelling Experts', desc: 'We turn boring corporate jargon into compelling narratives.' },
        { title: 'SEO Optimized', desc: 'Every piece of content is written to rank on Google.' },
        { title: 'Visuals Included', desc: 'We don\'t just write; we design custom graphics to support the text.' },
        { title: 'Consistency', desc: 'We deliver on time, every time, keeping your feed fresh.' },
        { title: 'Multi-Format', desc: 'We repurpose one blog into a video, an infographic, and 5 tweets.' },
        { title: 'Brand Alignment', desc: 'We adopt your brand voice perfectly, so it sounds like you.' }
    ];

    return (
        <div className="bg-white font-sans">
            {/* Hero Section */}
            <section className="relative h-[50vh] min-h-[400px] overflow-hidden">
                <div
                    className="absolute inset-0 bg-cover bg-center"
                    style={{ backgroundImage: `url('${heroImage}')` }}
                >
                    <div className="absolute inset-0 bg-gradient-to-r from-orange-900/90 via-red-900/80 to-transparent"></div>
                </div>

                <div className="relative h-full max-w-7xl mx-auto px-6 lg:px-12 flex items-center pt-20">
                    <motion.div
                        initial={{ opacity: 0, x: -50 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8 }}
                        className="max-w-2xl text-white"
                    >
                        <p className="text-xs font-light tracking-widest uppercase text-orange-300 mb-3">What We Do &gt; Creativity</p>
                        <h1 className="text-4xl md:text-5xl font-light mb-4 leading-tight">Content Marketing</h1>
                        <p className="text-base font-light text-gray-200 leading-relaxed mb-6">
                            Narratives that inspire action. We craft stories to build your brand authority.
                        </p>
                        <div className="flex flex-wrap gap-3">
                            <a href="#inquiry" className="px-5 py-2 bg-white text-orange-700 font-medium rounded-sm hover:shadow-xl transition-all text-sm whitespace-nowrap">
                                Get Content Plan
                            </a>
                            <a href="#services" className="px-5 py-2 border-2 border-white text-white font-medium rounded-sm hover:bg-white hover:text-orange-700 transition-all text-sm whitespace-nowrap">
                                View Samples
                            </a>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Intro Stats */}
            <section className="py-20 bg-slate-50">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="max-w-4xl mx-auto text-center mb-16">
                        <h2 className="text-4xl font-light text-slate-900 mb-6">The Power of Words</h2>
                        <p className="text-lg text-slate-600 leading-relaxed">
                            Content is the fuel for your entire marketing engine.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-4 gap-8 text-center divide-x divide-slate-200">
                        {[
                            { label: 'Words Written', val: '5M+' },
                            { label: 'VideosProduced', val: '500+' },
                            { label: 'Shares Generated', val: '1M+' },
                            { label: 'Leads Nurtured', val: '50k+' }
                        ].map((stat, idx) => (
                            <div key={idx} className="p-4">
                                <div className="text-4xl font-bold text-orange-600 mb-2">{stat.val}</div>
                                <div className="text-sm text-slate-500 uppercase tracking-wider">{stat.label}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Technologies / Platforms */}
            <section className="py-20 bg-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="text-center mb-12">
                        <h2 className="text-4xl font-light text-slate-900 mb-6">Distribution Channels</h2>
                        <p className="text-lg text-slate-600 max-w-3xl mx-auto">
                            We publish your content where it matters most.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-4 gap-6">
                        {platforms.map((plat, idx) => (
                            <motion.div
                                key={idx}
                                initial={{ opacity: 0, scale: 0.9 }}
                                whileInView={{ opacity: 1, scale: 1 }}
                                viewport={{ once: true }}
                                transition={{ delay: idx * 0.05 }}
                                className="p-6 bg-slate-50 rounded-sm border border-slate-200 text-center hover:shadow-lg transition-all group"
                            >
                                <plat.icon className="text-5xl mx-auto mb-4 text-orange-600 group-hover:scale-110 transition-transform" />
                                <h3 className="text-lg font-medium text-slate-900 mb-2">{plat.name}</h3>
                                <p className="text-sm text-slate-600">{plat.desc}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Services Grid (Hover Cards) */}
            <section id="services" className="py-20 bg-slate-50">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="text-center mb-12">
                        <h2 className="text-4xl font-light text-slate-900 mb-6">Content Services</h2>
                        <p className="text-lg text-slate-600 max-w-3xl mx-auto">
                            End-to-end production for all your content needs.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {services.map((service, idx) => (
                            <motion.div
                                key={idx}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: idx * 0.1 }}
                                className="group relative h-64 overflow-hidden rounded-sm border border-slate-200 cursor-pointer"
                            >
                                <div className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110" style={{ backgroundImage: `url(${service.image})` }}>
                                    <div className="absolute inset-0 bg-gradient-to-b from-black/50 to-black/80"></div>
                                </div>
                                <div className="absolute inset-0 p-6 flex flex-col justify-between text-white z-10">
                                    <div>
                                        <service.icon className="text-4xl mb-4 text-orange-300" />
                                        <h3 className="text-xl font-medium mb-2">{service.title}</h3>
                                    </div>
                                    <motion.div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-orange-600 to-red-600 p-6 transform translate-y-full group-hover:translate-y-0 transition-transform duration-500">
                                        <p className="text-white text-sm leading-relaxed">{service.desc}</p>
                                        <button className="mt-4 text-white font-medium hover:underline">Learn More →</button>
                                    </motion.div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Why Choose Us */}
            <section className="py-20 bg-gradient-to-br from-orange-600 via-red-600 to-pink-700 text-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="text-center mb-12">
                        <h2 className="text-4xl font-light mb-6">Why Our Content Works</h2>
                        <p className="text-lg text-orange-100 max-w-3xl mx-auto">
                            We write for humans, optimized for machines.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {whyChooseUs.map((item, idx) => (
                            <motion.div
                                key={idx}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: idx * 0.1 }}
                                className="bg-white/10 backdrop-blur-sm p-6 rounded-sm border border-white/20 hover:bg-white/20 transition-all"
                            >
                                <FaCheckCircle className="text-3xl text-orange-300 mb-4" />
                                <h3 className="text-xl font-medium mb-3">{item.title}</h3>
                                <p className="text-orange-100 leading-relaxed">{item.desc}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Timeline (Production Flow with Arrows) */}
            <section className="py-24 bg-slate-50 border-y border-slate-200">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="text-center mb-20">
                        <h2 className="text-3xl md:text-4xl font-light text-slate-900 mb-4">Production Workflow</h2>
                        <p className="text-slate-600">From brainstorm to published masterpiece.</p>
                    </div>

                    <div className="relative">
                        {/* Base Line (Grey) */}
                        <div className="hidden md:block absolute top-[40px] left-0 w-full h-1 bg-slate-200 md:rounded-full -z-0"></div>

                        {/* Steps Grid */}
                        <div className="grid grid-cols-2 md:grid-cols-6 gap-0 relative">
                            {flowSteps.map((step, idx) => (
                                <div key={idx} className="relative flex flex-col items-center group">

                                    {/* ARROW: Only show BETWEEN steps */}
                                    {idx < flowSteps.length - 1 && (
                                        <div className="hidden md:flex absolute top-[30px] -right-[50%] w-full justify-center items-center z-10 pointer-events-none">
                                            <motion.div
                                                animate={{ x: [-3, 3, -3] }}
                                                transition={{ repeat: Infinity, duration: 1.5, ease: "easeInOut" }}
                                            >
                                                <FaChevronRight className="text-2xl text-slate-400 font-bold" />
                                            </motion.div>
                                        </div>
                                    )}

                                    {/* Circle Icon */}
                                    <motion.div
                                        initial={{ opacity: 0, scale: 0.5, y: 20 }}
                                        whileInView={{ opacity: 1, scale: 1, y: 0 }}
                                        viewport={{ once: true }}
                                        transition={{ delay: idx * 0.1, type: "spring", stiffness: 100 }}
                                        className="relative z-20"
                                    >
                                        <div className={`w-20 h-20 rounded-full bg-gradient-to-br ${step.color} flex items-center justify-center text-white text-3xl shadow-lg mb-6 group-hover:scale-110 transition-transform duration-300 border-4 border-white ring-4 ring-slate-50`}>
                                            <step.icon />
                                        </div>
                                    </motion.div>

                                    {/* Number Badge */}
                                    <div className="absolute top-0 right-1/2 translate-x-10 -translate-y-0 w-7 h-7 rounded-full bg-slate-900 text-white text-xs font-bold flex items-center justify-center border-2 border-white shadow-md z-30">
                                        {idx + 1}
                                    </div>

                                    {/* Title */}
                                    <h3 className="text-lg font-bold text-slate-800 group-hover:text-orange-600 transition-colors mt-2">{step.title}</h3>

                                    {/* Mobile Vertical Line */}
                                    {idx < flowSteps.length - 1 && (
                                        <div className="md:hidden absolute bottom-[-40px] left-1/2 w-0.5 h-[40px] bg-slate-300"></div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* FAQ & Inquiry Form */}
            <section id="inquiry" className="py-20 bg-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="grid lg:grid-cols-2 gap-12">

                        {/* FAQs */}
                        <div>
                            <h2 className="text-3xl font-light text-slate-900 mb-8">Content FAQs</h2>
                            <div className="space-y-4">
                                {faqs.map((faq, idx) => (
                                    <motion.div
                                        key={idx}
                                        initial={{ opacity: 0, y: 10 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        viewport={{ once: true }}
                                        transition={{ delay: idx * 0.05 }}
                                        className="border border-slate-200 rounded-sm overflow-hidden"
                                    >
                                        <button
                                            onClick={() => toggleFaq(idx)}
                                            className="w-full px-6 py-4 text-left flex justify-between items-center bg-slate-50 hover:bg-slate-100 transition-colors"
                                        >
                                            <span className="font-medium text-slate-900">{faq.q}</span>
                                            {openFaq === idx ? <FaChevronUp className="text-orange-600" /> : <FaChevronDown className="text-slate-400" />}
                                        </button>
                                        {openFaq === idx && (
                                            <div className="px-6 py-4 bg-white">
                                                <p className="text-slate-600 leading-relaxed">{faq.a}</p>
                                            </div>
                                        )}
                                    </motion.div>
                                ))}
                            </div>
                        </div>

                        {/* Inquiry Form */}
                        <div className="bg-slate-50 p-8 rounded-sm border border-slate-200">
                            <h2 className="text-3xl font-light text-slate-900 mb-2">Tell Your Story</h2>
                            <p className="text-slate-600 mb-6">Let's create content that converts.</p>

                            <form className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">Full Name</label>
                                    <input type="text" className="w-full px-4 py-3 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-600" placeholder="John Doe" />
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                                        <input type="email" className="w-full px-4 py-3 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-600" placeholder="john@example.com" />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 mb-1">Phone</label>
                                        <input type="tel" className="w-full px-4 py-3 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-600" placeholder="+91 98765 43210" />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">Content Type</label>
                                    <select className="w-full px-4 py-3 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-600">
                                        <option>Blogs & Articles</option>
                                        <option>Video Production</option>
                                        <option>Social Graphics</option>
                                        <option>Full Strategy</option>
                                    </select>
                                </div>

                                <button type="submit" className="w-full bg-orange-600 text-white font-medium py-3 rounded-sm hover:bg-orange-700 transition-colors shadow-lg">
                                    Book Consult
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            </section>

            {/* Bottom CTA */}
            <section className="py-20 bg-gradient-to-r from-orange-700 to-red-800 text-white">
                <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
                    <h2 className="text-4xl md:text-5xl font-light mb-6">Need World-Class Content?</h2>
                    <p className="text-xl font-light mb-8 text-orange-100">
                        Stop boring your customers. Start engaging them.
                    </p>
                    <div className="flex flex-wrap gap-3 justify-center">
                        <a href="#inquiry" className="inline-block px-6 py-2.5 bg-white text-orange-800 font-medium rounded-sm hover:shadow-xl transition-all text-sm whitespace-nowrap">
                            Get Pricing
                        </a>
                        <a href="tel:+919876543210" className="inline-block px-6 py-2.5 border-2 border-white text-white font-medium rounded-sm hover:bg-white hover:text-orange-800 transition-all text-sm whitespace-nowrap">
                            Contact Editor
                        </a>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default ContentMarketingPage;
